import {
  CarrierPaymentDto,
  EnrollConnectPaymentRedirectDto,
} from '@zorro/clients';
import { Loader } from '@zorro/design-system/v2/Loader';
import { Center, Text } from '@zorro/web/design-system';
import { useMonolithQuery } from '@zorro/web/ui-utils';
import { useEffect } from 'react';

/** The carrier payment page only accepts a form POST (not a URL) — build and submit one. */
const submitPaymentRedirect = (redirect: EnrollConnectPaymentRedirectDto) => {
  const form = document.createElement('form');
  form.method = redirect.method;
  form.action = redirect.endpoint;

  for (const field of redirect.fields) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = field.name;
    input.value = field.value;
    form.append(input);
  }

  document.body.append(form);
  // A carrier field named `submit` would shadow `form.submit`.
  HTMLFormElement.prototype.submit.call(form);
};

type CarrierPaymentRedirectProps = {
  benefitId: string;
};

/**
 * Served from a route whose CSP allows cross-origin `form-action`, so the rest of the app keeps the
 * strict one. Fetches on mount because carrier redirects carry single-use, short-lived assertions.
 */
export const CarrierPaymentRedirect = ({
  benefitId,
}: CarrierPaymentRedirectProps) => {
  const { data: carrierPayment, isError } = useMonolithQuery({
    method: 'applicationsControllerGetPayment',
    params: [benefitId],
  });

  const paymentRedirect =
    carrierPayment?.channel === CarrierPaymentDto.channel.REDIRECT
      ? carrierPayment.redirect
      : undefined;

  useEffect(() => {
    if (paymentRedirect) {
      submitPaymentRedirect(paymentRedirect);
    }
  }, [paymentRedirect]);

  const isRedirectUnavailable = isError || (carrierPayment && !paymentRedirect);

  return (
    <Center h="100vh">
      {isRedirectUnavailable ? (
        <Text>
          We couldn&apos;t open the carrier payment page. Close this tab and try
          again.
        </Text>
      ) : (
        <Loader />
      )}
    </Center>
  );
};
